/**
 * The editorial dashboards (roadmap item 5).
 *
 * Eight questions an editor cannot answer from a collection list: what exists,
 * what is waiting on somebody, what has gone stale, what the gate objects to,
 * what shipped lately, what each category actually holds, which links are
 * broken, and which publishes did not fully succeed.
 *
 * Every row links to the guide it is about, through the side panel rather than
 * a URL, so following a finding does not lose the dashboard behind it. That is
 * the difference between a report and a to-do list.
 *
 * Everything is computed in `metrics.ts`, which is pure and tested. Nothing is
 * decided here: the gate decides what is missing, `internalLinks` decides what
 * a link is, and `readPublishState` decides what the pipeline said. A dashboard
 * that disagreed with the panel on the guide's own form would be worse than
 * none.
 */

import React, { useMemo, useState } from "react";
import { useSideEntityController } from "@firecms/core";
import {
    Alert,
    Button,
    CenteredView,
    Chip,
    CircularProgress,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
    Tabs,
    Typography
} from "@firecms/ui";
import { evaluatePublishGate, type GateIssue } from "@zennopay/schema";

import { guidesCollection } from "../collections/guides";
import { gateContextFor, knownRoutesFor, useGateReferenceData } from "../gate/useGateContext";
import { PublishStageChip } from "../publish/PublishStateBanner";
import { formatWhen, STAGE_DESCRIPTIONS } from "../publish/publishState";
import {
    brokenLinkReport,
    categoryCounts,
    contentOverview,
    metadataWarnings,
    overdueReviews,
    publishHealth,
    recentlyPublished,
    reviewPipeline,
    STATUSES,
    type EvaluatedGuide,
    type GuideRef
} from "./metrics";

/* --------------------------------------------------------------- shared -- */

/** The title of a guide, as a control that opens it. */
function GuideLink({ row }: { row: GuideRef }) {
    const sideEntityController = useSideEntityController();
    return (
        <div className={"flex flex-col items-start"}>
            <Button size={"small"}
                    variant={"text"}
                    className={"px-0 text-left"}
                    onClick={() => sideEntityController.open({
                        path: "guides",
                        entityId: row.id,
                        collection: guidesCollection as any
                    })}>
                {row.title}
            </Button>
            <Typography variant={"caption"} className={"opacity-50 font-mono"}>
                /guides/{row.slug || "—"}/
            </Typography>
        </div>
    );
}

function StatusChip({ status }: { status: string }) {
    return <Chip size={"small"}>{status}</Chip>;
}

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
    /* Fixed width rather than `grow`: a lone card on a wrapped second row would
       otherwise stretch to the full width and read as a heading. */
    return (
        <div className={"flex flex-col gap-1 rounded-md border border-surface-accent-200 " +
            "dark:border-surface-accent-700 p-4 w-52"}>
            <Typography variant={"h5"}>{value}</Typography>
            <Typography variant={"label"}>{label}</Typography>
            {hint && (
                <Typography variant={"caption"} className={"opacity-60"}>{hint}</Typography>
            )}
        </div>
    );
}

function Panel({ title, blurb, empty, children }: {
    title: string;
    blurb: string;
    empty?: string;
    children?: React.ReactNode;
}) {
    return (
        <section className={"flex flex-col gap-3"}>
            <div className={"flex flex-col gap-1"}>
                <Typography variant={"subtitle1"}>{title}</Typography>
                <Typography variant={"caption"} className={"opacity-70"}>{blurb}</Typography>
            </div>
            {children ?? (
                <Alert color={"success"} size={"small"}>{empty ?? "Nothing outstanding."}</Alert>
            )}
        </section>
    );
}

function IssueLines({ issues }: { issues: GateIssue[] }) {
    if (issues.length === 0) {
        return <Typography variant={"caption"} className={"opacity-40"}>—</Typography>;
    }
    return (
        <div className={"flex flex-col gap-1"}>
            {issues.map((issue, index) => (
                <div key={`${issue.code}:${index}`} className={"flex flex-row gap-2 items-baseline"}>
                    <Typography variant={"caption"}
                                className={"font-mono opacity-60 shrink-0"}>
                        {issue.code}
                    </Typography>
                    <Typography variant={"caption"}>{issue.message}</Typography>
                </div>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------- the view -- */

type TabKey =
    | "overview" | "pipeline" | "overdue" | "warnings"
    | "recent" | "categories" | "links" | "health";

export function DashboardsView() {

    const { data, loading, error } = useGateReferenceData();

    /* Fixed for the life of the view. "Eleven days overdue" recomputed on every
       render would be churn, and nobody watches a dashboard tick over. */
    const [now] = useState(() => Date.now());
    const [tab, setTab] = useState<TabKey>("overview");

    const knownRoutes = useMemo(() => knownRoutesFor(data.guides), [data.guides]);

    /* One gate evaluation per guide, shared by the warnings tab and the counts.
       The gate reads six collections' worth of context, which is why it is done
       once here rather than per row. */
    const evaluated: EvaluatedGuide[] = useMemo(
        () => data.guides.map((guide) => ({
            guide,
            result: evaluatePublishGate(gateContextFor(guide.id, guide, data, knownRoutes))
        })),
        [data, knownRoutes]
    );

    const overview = useMemo(() => contentOverview(data.guides, now), [data.guides, now]);
    const pipeline = useMemo(
        () => reviewPipeline(data.guides, data.authors, now), [data.guides, data.authors, now]);
    const overdue = useMemo(
        () => overdueReviews(data.guides, data.reviewers, now), [data.guides, data.reviewers, now]);
    const warnings = useMemo(() => metadataWarnings(evaluated), [evaluated]);
    const recent = useMemo(
        () => recentlyPublished(data.guides, data.authors, data.categories, now),
        [data.guides, data.authors, data.categories, now]);
    const categories = useMemo(
        () => categoryCounts(data.categories, data.guides), [data.categories, data.guides]);
    const links = useMemo(
        () => brokenLinkReport(data.guides, knownRoutes), [data.guides, knownRoutes]);
    const health = useMemo(() => publishHealth(data.guides), [data.guides]);

    if (loading) return <CenteredView><CircularProgress/></CenteredView>;

    if (error) {
        return (
            <CenteredView>
                <Alert color={"error"}>
                    The dashboards read every guide, category, claim, source, and person, and
                    that read failed: {error.message}
                </Alert>
            </CenteredView>
        );
    }

    const TABS: Array<{ key: TabKey; label: string; count?: number }> = [
        { key: "overview", label: "Overview" },
        { key: "pipeline", label: "Awaiting review", count: pipeline.length },
        { key: "overdue", label: "Overdue review", count: overdue.length },
        { key: "warnings", label: "Metadata & citations", count: warnings.length },
        { key: "recent", label: "Recently published", count: recent.length },
        { key: "categories", label: "Categories", count: categories.rows.length },
        { key: "links", label: "Broken links", count: links.length },
        { key: "health", label: "Publish health", count: health.length }
    ];

    return (
        <div className={"flex flex-col gap-6 p-8 max-w-6xl mx-auto w-full"}>

            <div className={"flex flex-col gap-2"}>
                <Typography variant={"h4"}>Dashboards</Typography>
                <Typography variant={"body2"} className={"opacity-70"}>
                    Read from every guide in the CMS at the moment this screen opened. The
                    warnings and the broken-link report are the publish gate&apos;s own verdicts,
                    evaluated here rather than restated, so they agree with the Publish gate tab
                    on each guide. Every row opens the guide it is about.
                </Typography>
            </div>

            <Tabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
                {TABS.map((entry) => (
                    <Tab key={entry.key} value={entry.key}>
                        {entry.label}{entry.count === undefined ? "" : ` (${entry.count})`}
                    </Tab>
                ))}
            </Tabs>

            {tab === "overview" && (
                <div className={"flex flex-col gap-6"}>
                    <div className={"flex flex-row gap-3 flex-wrap"}>
                        <Stat label={"Guides"} value={overview.total}
                              hint={"Every status, including archived."}/>
                        <Stat label={"Published"} value={overview.byStatus.published}
                              hint={`${overview.publishedReadingMinutes} minutes of reading live`}/>
                        <Stat label={"Awaiting someone"} value={pipeline.length}
                              hint={"In review, or approved and not published."}/>
                        <Stat label={"Overdue review"} value={overview.overdueReview}
                              hint={"nextReviewAt has passed."}/>
                        <Stat label={"Publish problems"} value={overview.unhealthy}
                              hint={"Blocked, stale, or degraded."}/>
                        <Stat label={"Scheduled"} value={overview.scheduled}
                              hint={"Carrying a scheduledPublishAt."}/>
                    </div>

                    <Panel title={"By status"} blurb={"Where everything sits in the workflow."}>
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Status</TableCell>
                                <TableCell header scope={"col"}>Guides</TableCell>
                            </TableHeader>
                            <TableBody>
                                {STATUSES.map((status) => (
                                    <TableRow key={status}>
                                        <TableCell><StatusChip status={status}/></TableCell>
                                        <TableCell>{overview.byStatus[status]}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Panel>

                    <Typography variant={"caption"} className={"opacity-60"}>
                        Last publish: {formatWhen(overview.lastPublishedAt)}
                        {" · "}{overview.featured} featured
                        {" · "}{overview.withoutHeroImage} published guide(s) with no hero image
                    </Typography>
                </div>
            )}

            {tab === "pipeline" && (
                <Panel title={"Waiting on somebody"}
                       blurb={
                           "Guides in review, and guides approved but never published. Both are " +
                           "stuck; the second is the one nobody remembers. Oldest first."
                       }
                       empty={"Nothing is waiting on a reviewer or a publisher."}>
                    {pipeline.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Guide</TableCell>
                                <TableCell header scope={"col"}>Waiting for</TableCell>
                                <TableCell header scope={"col"}>Author</TableCell>
                                <TableCell header scope={"col"}>Since</TableCell>
                            </TableHeader>
                            <TableBody>
                                {pipeline.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell><GuideLink row={row}/></TableCell>
                                        <TableCell>{row.waitingFor}</TableCell>
                                        <TableCell>{row.authorName ?? "—"}</TableCell>
                                        <TableCell>
                                            {row.waitingDays === undefined
                                                ? "—"
                                                : `${row.waitingDays} day(s)`}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : undefined}
                </Panel>
            )}

            {tab === "overdue" && (
                <Panel title={"Overdue review queue"}
                       blurb={
                           "Guides whose nextReviewAt has passed. Published or not: content that " +
                           "outlived its review date is stale either way, and the gate raises a " +
                           "warning on it at publish time."
                       }
                       empty={"Nothing is past its review date."}>
                    {overdue.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Guide</TableCell>
                                <TableCell header scope={"col"}>Status</TableCell>
                                <TableCell header scope={"col"}>Due</TableCell>
                                <TableCell header scope={"col"}>Overdue</TableCell>
                                <TableCell header scope={"col"}>Reviewer</TableCell>
                            </TableHeader>
                            <TableBody>
                                {overdue.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell><GuideLink row={row}/></TableCell>
                                        <TableCell><StatusChip status={row.status}/></TableCell>
                                        <TableCell>{formatWhen(row.nextReviewAt)}</TableCell>
                                        <TableCell>{row.daysOverdue} day(s)</TableCell>
                                        <TableCell>{row.reviewerName ?? "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : undefined}
                </Panel>
            )}

            {tab === "warnings" && (
                <Panel title={"Missing metadata and citation warnings"}
                       blurb={
                           "The publish gate's verdict on every guide, evaluated here. Blocking " +
                           "issues refuse a publish; the rest are worth fixing anyway. Broken " +
                           "links have their own tab, so they are not counted twice."
                       }
                       empty={"The gate has nothing to say about any guide."}>
                    {warnings.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Guide</TableCell>
                                <TableCell header scope={"col"}>Blocking</TableCell>
                                <TableCell header scope={"col"}>Metadata</TableCell>
                                <TableCell header scope={"col"}>Evidence</TableCell>
                            </TableHeader>
                            <TableBody>
                                {warnings.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <GuideLink row={row}/>
                                            <div className={"flex flex-row gap-2 pt-1"}>
                                                <StatusChip status={row.status}/>
                                                {row.waived > 0 && (
                                                    <Chip size={"small"} colorScheme={"blueLighter"}>
                                                        {row.waived} waived
                                                    </Chip>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {row.blocking > 0
                                                ? <Chip size={"small"} colorScheme={"redLighter"}>
                                                    {row.blocking}
                                                </Chip>
                                                : <Typography variant={"caption"}
                                                              className={"opacity-40"}>0</Typography>}
                                        </TableCell>
                                        <TableCell><IssueLines issues={row.metadata}/></TableCell>
                                        <TableCell>
                                            <IssueLines issues={[...row.citations, ...row.other]}/>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : undefined}
                </Panel>
            )}

            {tab === "recent" && (
                <Panel title={"Recently published"}
                       blurb={"Newest first, by publish date. Undated guides sort last."}
                       empty={"Nothing has been published yet."}>
                    {recent.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Guide</TableCell>
                                <TableCell header scope={"col"}>Published</TableCell>
                                <TableCell header scope={"col"}>Category</TableCell>
                                <TableCell header scope={"col"}>Author</TableCell>
                                <TableCell header scope={"col"}>By</TableCell>
                            </TableHeader>
                            <TableBody>
                                {recent.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell><GuideLink row={row}/></TableCell>
                                        <TableCell>
                                            {formatWhen(row.publishedAt)}
                                            {row.daysAgo !== undefined && (
                                                <Typography variant={"caption"} className={"opacity-60"}>
                                                    {row.daysAgo} day(s) ago
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{row.categoryName ?? "—"}</TableCell>
                                        <TableCell>{row.authorName ?? "—"}</TableCell>
                                        <TableCell>{row.publishedBy ?? "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : undefined}
                </Panel>
            )}

            {tab === "categories" && (
                <div className={"flex flex-col gap-6"}>
                    <Panel title={"Categories, with guide counts"}
                           blurb={
                               "The hub renders a section per category that has something " +
                               "published in it; `buildSnapshot` omits the rest. A visible " +
                               "category with nothing published renders nowhere."
                           }>
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Category</TableCell>
                                <TableCell header scope={"col"}>Published</TableCell>
                                <TableCell header scope={"col"}>Drafts</TableCell>
                                <TableCell header scope={"col"}>Total</TableCell>
                                <TableCell header scope={"col"}>On the hub</TableCell>
                            </TableHeader>
                            <TableBody>
                                {categories.rows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>
                                            <Typography variant={"body2"}>{row.name}</Typography>
                                            <Typography variant={"caption"}
                                                        className={"opacity-50 font-mono"}>
                                                /{row.slug}/
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{row.published}</TableCell>
                                        <TableCell>{row.drafts}</TableCell>
                                        <TableCell>{row.total}</TableCell>
                                        <TableCell>
                                            {!row.isVisible
                                                ? <Chip size={"small"}>hidden</Chip>
                                                : row.emptyOnTheHub
                                                    ? <Chip size={"small"} colorScheme={"orangeLighter"}>
                                                        nothing published
                                                    </Chip>
                                                    : <Chip size={"small"} colorScheme={"greenLighter"}>
                                                        rendered
                                                    </Chip>}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Panel>

                    {categories.orphaned.length > 0 && (
                        <Panel title={"Guides with no such category"}
                               blurb={
                                   "These point at a category id that does not exist, so the " +
                                   "exporter groups them nowhere and they never reach the hub."
                               }>
                            <Table>
                                <TableHeader>
                                    <TableCell header scope={"col"}>Guide</TableCell>
                                    <TableCell header scope={"col"}>Status</TableCell>
                                </TableHeader>
                                <TableBody>
                                    {categories.orphaned.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell><GuideLink row={row}/></TableCell>
                                            <TableCell><StatusChip status={row.status}/></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Panel>
                    )}
                </div>
            )}

            {tab === "links" && (
                <Panel title={"Broken internal links"}
                       blurb={
                           "Hrefs in a guide body that the site does not serve, found with the " +
                           "same `internalLinks` the publish gate uses. A broken link blocks " +
                           "publishing and cannot be waived by a migration exemption."
                       }
                       empty={"Every internal link points at a route the site serves."}>
                    {links.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Guide</TableCell>
                                <TableCell header scope={"col"}>Status</TableCell>
                                <TableCell header scope={"col"}>Links the site does not serve</TableCell>
                            </TableHeader>
                            <TableBody>
                                {links.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell><GuideLink row={row}/></TableCell>
                                        <TableCell><StatusChip status={row.status}/></TableCell>
                                        <TableCell>
                                            <div className={"flex flex-col gap-1"}>
                                                {row.links.map((link) => (
                                                    <Typography key={link} variant={"caption"}
                                                                className={"font-mono"}>
                                                        {link}
                                                    </Typography>
                                                ))}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : undefined}
                </Panel>
            )}

            {tab === "health" && (
                <Panel title={"Publish health"}
                       blurb={
                           "Guides whose last publish attempt was blocked, went stale, or " +
                           "degraded. A stale guide is the one to read first: it is live, and " +
                           "the site is serving the version from before the edit."
                       }
                       empty={"Every guide's last publish attempt completed."}>
                    {health.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableCell header scope={"col"}>Guide</TableCell>
                                <TableCell header scope={"col"}>Stage</TableCell>
                                <TableCell header scope={"col"}>What it means</TableCell>
                                <TableCell header scope={"col"}>Reported</TableCell>
                            </TableHeader>
                            <TableBody>
                                {health.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell><GuideLink row={row}/></TableCell>
                                        <TableCell>
                                            <PublishStageChip state={{
                                                stage: row.stage,
                                                message: row.message,
                                                updatedAt: row.updatedAt,
                                                actor: row.actor
                                            }}/>
                                        </TableCell>
                                        <TableCell>
                                            <div className={"flex flex-col gap-1"}>
                                                <Typography variant={"caption"}>
                                                    {STAGE_DESCRIPTIONS[row.stage].headline}
                                                </Typography>
                                                {row.message && (
                                                    <Typography variant={"caption"} className={"opacity-70"}>
                                                        {row.message}
                                                    </Typography>
                                                )}
                                                {row.issues.length > 0 && (
                                                    <IssueLines issues={row.issues}/>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant={"caption"}>
                                                {formatWhen(row.updatedAt)}
                                            </Typography>
                                            <Typography variant={"caption"} className={"opacity-60"}>
                                                {row.actor || "—"}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : undefined}
                </Panel>
            )}
        </div>
    );
}
